import React, { useEffect, useMemo, useState } from "react";
import WeeklyRevenue from "views/admin/default/components/WeeklyRevenue";
import TotalSpent from "views/admin/default/components/TotalSpent";
import PieChartCard from "views/admin/default/components/PieChartCard";
import { columnsDataCheck } from "./variables/columnsData";
import CheckTable from "views/admin/default/components/CheckTable";
import DailyTraffic from "views/admin/default/components/DailyTraffic";
import tableDataCheck from "./variables/tableDataCheck.json";

import {
  getAttendance,
  getEmployees,
  getLiveLocations,
} from "../../../services/hrms.api";
import Loading from "components/common/Loading";

const fmtHM = (mins) => {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}h ${String(mm).padStart(2, "0")}m`;
};
const ymd = (v) => {
  const d = new Date(v);
  if (isNaN(d)) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const pickEmployeeIdFromDoc = (doc) => {
  const e = doc?.employee;
  if (!e) return "";
  if (typeof e === "string") return e; // ObjectId string
  if (typeof e === "object") return String(e._id || e.id || e.code || ""); // populated doc
  return "";
};

/* ------------- derive computed attendance view fields ------------- */
const msToMin = (ms) => Math.max(0, Math.round((ms || 0) / 60000));

const computeViewFromAttendance = (doc) => {
  const workDay = doc?.workDay || ymd(doc?.createdAt) || ymd(Date.now());

  // sessions are still useful as fallback for clock in/out
  const sessions = Array.isArray(doc?.sessions) ? doc.sessions : [];

  const firstSession = sessions[0];
  const lastSession = sessions[sessions.length - 1];

  // Prefer explicit fields from backend; fallback to sessions
  const rawIn = doc?.clockIn || firstSession?.in || null;
  const rawOut = doc?.clockOut || lastSession?.out || null;

  const workedMin = msToMin(doc?.workedMs);
  const breakMin = msToMin(doc?.breakMs);

  const fmt12 = (v) => {
    const dt = v ? new Date(v) : null;
    return dt instanceof Date && !isNaN(dt)
      ? dt.toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
  };

  const state = String(doc?.state || "").toUpperCase();
  const isLive = state === "IN" || state === "BREAK";

  // Status: if there is any worked time or a punch-in, mark Present
  const status = isLive || workedMin > 0 || rawIn ? "Present" : "Absent";

  const employeeId = pickEmployeeIdFromDoc(doc);
  const empPop = typeof doc?.employee === "object" ? doc.employee : null;

  return {
    _id: String(doc._id || ""),
    employeeId,
    _empPop: empPop,

    date: `${workDay}T00:00:00.000Z`,
    workDay,

    in: fmt12(rawIn) || "",
    out: fmt12(rawOut) || "",
    hours: fmtHM(workedMin), // ✅ uses backed workedMs, already capped
    status,

    _breakMin: breakMin,
    _totalMin: workedMin,
    _firstIn: rawIn ? new Date(rawIn) : null,
    _lastOut: rawOut ? new Date(rawOut) : null,
    _live: isLive,
  };
};

// return a valid Date or null from multiple fallbacks
const pickDate = (r) => {
  const candidates = [
    r?.date,
    r?.workDay && `${r.workDay}T00:00:00Z`, // server-side workDay
    r?.sessions?.[0]?.in,
    r?.createdAt,
    r?.updatedAt,
  ].filter(Boolean);
  for (const c of candidates) {
    const d = new Date(c);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
};

const Dashboard = () => {
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // load attendance + employee + gps
  const load = async () => {
    setLoading(true);
    try {
      const [{ data: a }, { data: e }] = await Promise.all([
        getAttendance({ limit: 200 }),
        getEmployees(),
        getLiveLocations(),
      ]);

      // attendance
      const raw = Array.isArray(a) ? a : a?.items || [];
      const mapped = raw.map(computeViewFromAttendance);
      setRows(mapped);

      // employees
      setEmployees(Array.isArray(e) ? e : e?.items || []);

      // console.log("Normalized OFFICE LAT AND LANG Data:", officeCenter);
    } catch (err) {
      console.error("[AttendancePage] load error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // KPIs (compare using YYYY-MM-DD)
  const todayIso = useMemo(() => ymd(new Date()), []);
  const checkedInTodayIds = useMemo(
    () =>
      new Set(
        rows
          .filter((r) => {
            const day = ymd(pickDate(r));
            return (
              day === todayIso &&
              (r.in || String(r.status || "").toLowerCase() === "present")
            );
          })
          .map((r) => String(r.employeeId || r.employee || ""))
      ),
    [rows, todayIso]
  );

  const onLeaveTodayIds = useMemo(
    () =>
      new Set(
        rows
          .filter((r) => {
            const day = ymd(pickDate(r));
            return (
              day === todayIso &&
              String(r.status || "").toLowerCase() === "leave"
            );
          })
          .map((r) => String(r.employeeId || r.employee || ""))
      ),
    [rows, todayIso]
  );

  const totalEmployees = employees.length;
  const checkedInToday = checkedInTodayIds.size;
  const onLeaveToday = onLeaveTodayIds.size;
  const notCheckedIn = Math.max(
    0,
    totalEmployees - (checkedInToday + onLeaveToday)
  );

  const getGreeting = (date = new Date()) => {
    const h = date.getHours();
    if (h < 5) return "Good Night";
    if (h < 12) return "Good Morning";
    if (h < 17) return "Good Afternoon";
    if (h < 21) return "Good Evening";
    return "Good Night";
  };

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    // update every 30s is enough for minutes; use 1000 for seconds
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const greeting = getGreeting(now);

  if (loading) {
    return <Loading variant="dots" fullscreen />;
  }

  return (
    <div>
      {/* KPIs */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5 p-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800 ">
          <div className="text-sm text-gray-500">{greeting}, Admin</div>

          <div className="mt-1 text-2xl font-semibold dark:text-white">
            {now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            ,{" "}
            {now.toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })}
          </div>
        </div>

        <KpiCard
          title="Total Employees"
          value={totalEmployees}
          note="Org size"
          color="indigo"
        />
        <KpiCard
          title="Checked In Today"
          value={checkedInToday}
          note="Present"
          color="green"
        />
        <KpiCard
          title="On Leave"
          value={onLeaveToday}
          note="Today"
          color="amber"
        />
        <KpiCard
          title="Not Checked In"
          value={notCheckedIn}
          note="Pending"
          color="indigo"
        />
      </div>
      {/* Charts */}

      <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
        <TotalSpent />
        <WeeklyRevenue />
      </div>

      {/* Tables & Charts */}

      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-2">
        {/* Check Table */}
        <div>
          <CheckTable
            columnsData={columnsDataCheck}
            tableData={tableDataCheck}
          />
        </div>

        {/* Traffic chart & Pie Chart */}

        <div className="grid grid-cols-1 gap-5 rounded-[20px] md:grid-cols-2">
          <DailyTraffic />
          <PieChartCard />
        </div>
      </div>
    </div>
  );
};

function KpiCard({ title, value, note, color = "indigo" }) {
  const colorMap = {
    indigo: "bg-indigo-100 text-indigo-700",
    green: "bg-green-100 text-green-700",
    amber: "bg-amber-100 text-amber-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
      <div className="text-sm text-gray-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold dark:text-white">{value}</div>
      {note && (
        <div
          className={`mt-2 inline-block rounded-lg px-2 py-1 text-xs ${colorMap[color]}`}
        >
          {note}
        </div>
      )}
    </div>
  );
}

export default Dashboard;
